// BUILD-042 (Oct 23) - the Benefits workspace for a BENEFIT MANAGER who is not an HR administrator.
//
// A person holding hr.benefit.manage (company scope) but NOT hr.org.manage cannot open HR Administration (its roster and employee pages are, correctly,
// refused for them). This small workspace reaches exactly the two benefit screens and nothing else:
//   * Plans                - the existing BenefitPlansPanel (benefit information);
//   * Employee eligibility - pick a person, then the existing AdminBenefitsEligibilitySection (the review outcome per plan).
// The person picker is fed by ONE narrow endpoint that returns names only (no email, department, title, status, dates or record details). Every
// action re-authorizes on the server (hr.benefit.manage, company scope); this screen only reflects what the server already allows. It does not import
// HR Administration, the roster, or the employee-detail workspace, and it makes no request to the HR roster or employee-detail routes.
import { useEffect, useState } from 'react';
import { listHrBenefitEligibilityPeople } from '../../api/client';
import { BenefitPlansPanel } from './BenefitPlansPanel';
import { AdminBenefitsEligibilitySection } from './workspace/AdminBenefitsEligibilitySection';

type Tab = 'plans' | 'eligibility';

export function BenefitsManagerWorkspace() {
  const [tab, setTab] = useState<Tab>('plans');
  const [people, setPeople] = useState<Array<{ employeeId: string; name: string }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (tab !== 'eligibility' || people) return;
    listHrBenefitEligibilityPeople()
      .then((d) => { setPeople(d.people); setError(null); })
      .catch(() => setError('People could not be loaded right now.'));
  }, [tab, people]);

  const needle = search.trim().toLowerCase();
  const shown = (people ?? []).filter((p) => !needle || p.name.toLowerCase().includes(needle));
  const selected = (people ?? []).find((p) => p.employeeId === selectedId) ?? null;

  const tabClass = (t: Tab) =>
    `text-sm pb-2 border-b-2 -mb-px ${tab === t ? 'text-[#4AABDE] border-[#4AABDE] font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`;

  return (
    <div className="max-w-[980px]" data-testid="benefits-manager-workspace">
      <h1 className="text-xl font-bold text-gray-900 leading-tight">Benefits</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">Manage benefit plan information and record each person’s benefit eligibility review. This is not enrollment.</p>

      <div role="tablist" aria-label="Benefits sections" className="flex flex-wrap gap-x-6 gap-y-1 border-b border-gray-200 mb-6">
        <button type="button" role="tab" aria-selected={tab === 'plans'} className={tabClass('plans')} onClick={() => setTab('plans')} data-testid="benefits-tab-plans">Plans</button>
        <button type="button" role="tab" aria-selected={tab === 'eligibility'} className={tabClass('eligibility')} onClick={() => setTab('eligibility')} data-testid="benefits-tab-eligibility">Employee eligibility</button>
      </div>

      {tab === 'plans' && <BenefitPlansPanel canListDocuments={false} />}

      {tab === 'eligibility' && (
        <div className="flex flex-col sm:flex-row gap-5">
          <aside className="w-full sm:w-64 flex-shrink-0">
            <label htmlFor="benefit-person-search" className="text-xs text-gray-500 block mb-1">Find a person</label>
            <input
              id="benefit-person-search"
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-2"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {error && <div role="alert" className="text-sm text-red-700">{error}</div>}
            {!error && people === null && <div className="text-sm text-gray-500">Loading…</div>}
            {!error && people !== null && shown.length === 0 && <div className="text-sm text-gray-500" data-testid="benefit-people-empty">No matching people.</div>}
            <ul className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-[28rem] overflow-y-auto" data-testid="benefit-people-list">
              {shown.map((p) => (
                <li key={p.employeeId}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.employeeId)}
                    aria-current={p.employeeId === selectedId ? 'true' : undefined}
                    className={`w-full text-left px-3 py-2 text-sm ${p.employeeId === selectedId ? 'bg-[#EDF5FC] font-semibold text-gray-900' : 'text-gray-800 hover:bg-gray-50'}`}
                  >
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <main className="flex-1 min-w-0">
            {selected
              ? <AdminBenefitsEligibilitySection key={selected.employeeId} employeeId={selected.employeeId} employeeName={selected.name} />
              : <div className="text-sm text-gray-500 bg-white rounded-lg border border-gray-200 p-5">Choose a person to review their benefit eligibility.</div>}
          </main>
        </div>
      )}
    </div>
  );
}
